import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Popover,
  Stack,
  Divider,
  CircularProgress,
  Tooltip,
  Paper,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  WbSunny as SunIcon,
  CloudQueue as CloudIcon,
  Thunderstorm as ThunderIcon,
  AcUnit as SnowIcon,
  WaterDrop as RainIcon,
  Grain as DrizzleIcon,
  Refresh as RefreshIcon,
  LocationOn as LocationIcon,
  MyLocation as MyLocationIcon,
  AccessTime as ClockIcon,
  Air as WindIcon,
  Opacity as HumidityIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

// WMO Weather interpretation codes
const WMO_WEATHER_MAP = {
  0: { label: "Clear sky", icon: "☀️", color: "#f59e0b" },
  1: { label: "Mainly clear", icon: "🌤️", color: "#f59e0b" },
  2: { label: "Partly cloudy", icon: "⛅", color: "#94a3b8" },
  3: { label: "Overcast", icon: "☁️", color: "#64748b" },
  45: { label: "Foggy", icon: "🌫️", color: "#94a3b8" },
  48: { label: "Depositing rime fog", icon: "🌫️", color: "#94a3b8" },
  51: { label: "Light drizzle", icon: "🌦️", color: "#38bdf8" },
  53: { label: "Moderate drizzle", icon: "🌦️", color: "#38bdf8" },
  55: { label: "Dense drizzle", icon: "🌧️", color: "#0284c7" },
  61: { label: "Slight rain", icon: "🌧️", color: "#38bdf8" },
  63: { label: "Moderate rain", icon: "🌧️", color: "#0284c7" },
  65: { label: "Heavy rain", icon: "🌧️", color: "#0369a1" },
  71: { label: "Slight snow", icon: "❄️", color: "#e0f2fe" },
  73: { label: "Moderate snow", icon: "❄️", color: "#bae6fd" },
  75: { label: "Heavy snow", icon: "❄️", color: "#7dd3fc" },
  80: { label: "Rain showers", icon: "🌦️", color: "#0284c7" },
  81: { label: "Heavy showers", icon: "🌧️", color: "#0369a1" },
  82: { label: "Violent showers", icon: "⛈️", color: "#1e3a8a" },
  95: { label: "Thunderstorm", icon: "⛈️", color: "#eab308" },
  96: { label: "Thunderstorm with hail", icon: "⛈️", color: "#eab308" },
  99: { label: "Severe thunderstorm", icon: "⛈️", color: "#ca8a04" },
};

function getWeatherMeta(code) {
  return WMO_WEATHER_MAP[code] || { label: "Clear sky", icon: "☀️", color: "#f59e0b" };
}

// Session & Local storage cache keys
const CACHE_KEY = "goafreet_live_weather_v1";
const CACHE_TTL_MS = 15 * 60 * 1000;
const EXACT_LOC_KEY = "goafreet_user_exact_location_v1";

export default function WeatherClockWidget({ variant = "header" }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Real-time ticking clock state
  const [currentTime, setCurrentTime] = useState(null);
  
  // Weather state
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [geoLoading, setGeoLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // 1. Digital Clock Ticking (Every 1000ms)
  useEffect(() => {
    setCurrentTime(new Date());
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Helper to fetch Open-Meteo weather for coordinates
  const fetchWeatherForCoords = useCallback(
    async (lat, lon, city, country, source = "IP") => {
      try {
        const weatherUrl = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m,apparent_temperature,weather_code,wind_speed_10m&daily=weather_code,temperature_2m_max,temperature_2m_min&timezone=auto`;
        const wRes = await fetch(weatherUrl);
        if (wRes.ok) {
          const wData = await wRes.json();
          const currentMeta = getWeatherMeta(wData.current.weather_code);

          // Parse next 4 days forecast
          const forecastList = [];
          if (wData.daily && wData.daily.time) {
            for (let i = 1; i <= Math.min(4, wData.daily.time.length - 1); i++) {
              const dateObj = new Date(wData.daily.time[i]);
              const dayName = dateObj.toLocaleDateString("en-US", { weekday: "short" });
              const dayMeta = getWeatherMeta(wData.daily.weather_code[i]);
              forecastList.push({
                day: dayName,
                maxTemp: Math.round(wData.daily.temperature_2m_max[i]),
                minTemp: Math.round(wData.daily.temperature_2m_min[i]),
                condition: dayMeta.label,
                icon: dayMeta.icon,
              });
            }
          }

          const assembledWeather = {
            city,
            country,
            lat,
            lon,
            source,
            temp: Math.round(wData.current.temperature_2m),
            feelsLike: Math.round(wData.current.apparent_temperature),
            humidity: wData.current.relative_humidity_2m,
            windSpeed: Math.round(wData.current.wind_speed_10m),
            condition: currentMeta.label,
            icon: currentMeta.icon,
            forecast: forecastList,
          };

          setWeather(assembledWeather);
          try {
            sessionStorage.setItem(
              CACHE_KEY,
              JSON.stringify({ timestamp: Date.now(), data: assembledWeather })
            );
          } catch (e) {}
          return assembledWeather;
        }
      } catch (err) {
        console.warn("Weather fetch error:", err);
      }
      return null;
    },
    []
  );

  // 2. Fetch Geolocation and Weather
  const fetchWeather = useCallback(
    async (force = false) => {
      if (typeof window === "undefined") return;

      // Check session cache if not forcing
      if (!force) {
        try {
          const cached = sessionStorage.getItem(CACHE_KEY);
          if (cached) {
            const parsed = JSON.parse(cached);
            if (Date.now() - parsed.timestamp < CACHE_TTL_MS) {
              setWeather(parsed.data);
              setLoading(false);
              return;
            }
          }
        } catch (e) {}
      }

      setLoading(true);

      // A. Check if user already saved an exact GPS location
      try {
        const savedLoc = localStorage.getItem(EXACT_LOC_KEY);
        if (savedLoc) {
          const parsedLoc = JSON.parse(savedLoc);
          if (parsedLoc.lat && parsedLoc.lon) {
            const w = await fetchWeatherForCoords(
              parsedLoc.lat,
              parsedLoc.lon,
              parsedLoc.city || "Vadodara",
              parsedLoc.country || "India",
              "GPS"
            );
            if (w) {
              setLoading(false);
              return;
            }
          }
        }
      } catch (e) {}

      // B. Fallback location: Vadodara (Goafreet Headquarters)
      let lat = 22.3072;
      let lon = 73.1812;
      let city = "Vadodara";
      let country = "India";
      let source = "IP";

      // C. Attempt IP resolution (Note: Gujarati ISPs frequently route through Ahmedabad gateway)
      try {
        const ipRes = await fetch("https://ipwho.is/");
        if (ipRes.ok) {
          const ipData = await ipRes.json();
          if (ipData.success && ipData.latitude && ipData.longitude) {
            lat = ipData.latitude;
            lon = ipData.longitude;
            city = ipData.city || ipData.region || "Vadodara";
            country = ipData.country || "India";
          }
        }
      } catch (e) {
        try {
          const geoRes = await fetch("https://get.geojs.io/v1/ip/geo.json");
          if (geoRes.ok) {
            const geoData = await geoRes.json();
            if (geoData.latitude && geoData.longitude) {
              lat = parseFloat(geoData.latitude);
              lon = parseFloat(geoData.longitude);
              city = geoData.city || "Vadodara";
              country = geoData.country || "India";
            }
          }
        } catch (err2) {}
      }

      await fetchWeatherForCoords(lat, lon, city, country, source);
      setLoading(false);
    },
    [fetchWeatherForCoords]
  );

  // 3. User-Triggered Exact GPS Location Resolution
  const requestExactLocation = useCallback(() => {
    if (typeof window === "undefined" || !navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }

    setGeoLoading(true);

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const lat = pos.coords.latitude;
          const lon = pos.coords.longitude;
          let detectedCity = "Vadodara";
          let detectedCountry = "India";

          // Reverse geocode high-precision GPS coordinates using BigDataCloud
          try {
            const revRes = await fetch(
              `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=en`
            );
            if (revRes.ok) {
              const revData = await revRes.json();
              detectedCity =
                revData.city ||
                revData.locality ||
                revData.principalSubdivision ||
                "Vadodara";
              detectedCountry = revData.countryName || "India";
            }
          } catch (e) {
            console.warn("Reverse geocode fallback error:", e);
          }

          // Persist exact GPS in localStorage so all subsequent visits accurately show Vadodara
          try {
            localStorage.setItem(
              EXACT_LOC_KEY,
              JSON.stringify({
                lat,
                lon,
                city: detectedCity,
                country: detectedCountry,
                source: "GPS",
              })
            );
          } catch (e) {}

          await fetchWeatherForCoords(lat, lon, detectedCity, detectedCountry, "GPS");
        } catch (e) {
          console.error("GPS resolution error:", e);
        } finally {
          setGeoLoading(false);
        }
      },
      (err) => {
        setGeoLoading(false);
        console.warn("Geolocation permission error:", err);
        if (err.code === 1) {
          alert(
            "Location permission was denied. Please allow location permissions in your browser address bar settings to detect your exact city."
          );
        } else {
          alert("Unable to fetch exact GPS coordinates. Please ensure location services are enabled on your device.");
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }, [fetchWeatherForCoords]);

  useEffect(() => {
    fetchWeather();
  }, [fetchWeather]);

  // Clock formatters
  const formatTime = (date) => {
    if (!date) return "--:--:--";
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const formatDate = (date) => {
    if (!date) return "";
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      day: "2-digit",
      month: "short",
    });
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // ============================================================================
  // VARIANT: FOOTER
  // ============================================================================
  if (variant === "footer") {
    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1.5,
          py: 0.8,
          px: 1.8,
          borderRadius: "999px",
          bgcolor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
          border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.08)",
          fontSize: "0.82rem",
          fontWeight: 600,
          color: "text.secondary",
        }}
      >
        {/* Pulsing Green Live Dot */}
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            bgcolor: "#10b981",
            boxShadow: "0 0 0 2px rgba(16, 185, 129, 0.3)",
            animation: "pulse 2s infinite",
            "@keyframes pulse": {
              "0%": { transform: "scale(0.95)", boxShadow: "0 0 0 0 rgba(16, 185, 129, 0.7)" },
              "70%": { transform: "scale(1)", boxShadow: "0 0 0 6px rgba(16, 185, 129, 0)" },
              "100%": { transform: "scale(0.95)", boxShadow: "0 0 0 0 rgba(16, 185, 129, 0)" },
            },
          }}
        />

        {/* Real-Time Digital Clock */}
        <Typography
          variant="caption"
          sx={{
            fontFamily: "monospace",
            fontWeight: 800,
            fontSize: "0.85rem",
            color: "text.primary",
            letterSpacing: "0.5px",
          }}
        >
          {formatTime(currentTime)}
        </Typography>

        <Divider orientation="vertical" flexItem sx={{ height: 14, my: "auto" }} />

        {/* Location & Temp */}
        {loading ? (
          <CircularProgress size={12} sx={{ color: "text.secondary" }} />
        ) : weather ? (
          <Tooltip title={`Live weather for ${weather.city}, ${weather.country}: ${weather.condition}`}>
            <Box
              component="button"
              onClick={handleClick}
              sx={{
                background: "none",
                border: "none",
                padding: 0,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.6,
                color: "inherit",
                cursor: "pointer",
                fontFamily: "inherit",
                "&:hover": { color: "primary.main" },
              }}
            >
              <span>{weather.icon}</span>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "text.primary" }}>
                {weather.temp}°C
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {weather.city}
              </Typography>
            </Box>
          </Tooltip>
        ) : null}

        {/* Forecast Popover modal */}
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          transformOrigin={{ vertical: "bottom", horizontal: "center" }}
          PaperProps={{
            sx: {
              borderRadius: "20px",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.4)",
              overflow: "hidden",
              border: "none",
            },
          }}
        >
          <WeatherCardModal
            weather={weather}
            currentTime={currentTime}
            formatTime={formatTime}
            formatDate={formatDate}
            onClose={handleClose}
            onRefresh={() => fetchWeather(true)}
            onRequestExactLocation={requestExactLocation}
            geoLoading={geoLoading}
          />
        </Popover>
      </Box>
    );
  }

  // ============================================================================
  // VARIANT: HEADER (TOP MENU BAR)
  // ============================================================================
  return (
    <>
      <Tooltip title="Click to view live city weather forecast & details">
        <Box
          component="button"
          onClick={handleClick}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1.2,
            py: 0.65,
            px: 1.6,
            borderRadius: "999px",
            background: isDark
              ? "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)"
              : "linear-gradient(135deg, rgba(248, 250, 252, 0.9) 0%, rgba(241, 245, 249, 0.8) 100%)",
            border: isDark ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #e2e8f0",
            backdropFilter: "blur(12px)",
            boxShadow: isDark
              ? "0 4px 12px rgba(0, 0, 0, 0.3)"
              : "0 2px 8px rgba(0, 0, 0, 0.05)",
            cursor: "pointer",
            transition: "all 0.25s ease",
            color: "text.primary",
            maxWidth: "100%",
            minWidth: 0,
            whiteSpace: "nowrap",
            "&:hover": {
              transform: "translateY(-1px)",
              borderColor: "#f97316",
              boxShadow: "0 6px 16px rgba(249, 115, 22, 0.2)",
            },
          }}
        >
          {/* Pulsing Live Indicator */}
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              bgcolor: "#10b981",
              boxShadow: "0 0 0 2px rgba(16, 185, 129, 0.25)",
              animation: "pulse 2s infinite",
              "@keyframes pulse": {
                "0%": { transform: "scale(0.95)", boxShadow: "0 0 0 0 rgba(16, 185, 129, 0.7)" },
                "70%": { transform: "scale(1)", boxShadow: "0 0 0 6px rgba(16, 185, 129, 0)" },
                "100%": { transform: "scale(0.95)", boxShadow: "0 0 0 0 rgba(16, 185, 129, 0)" },
              },
            }}
          />

          {/* Real-Time Digital Watch */}
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <ClockIcon sx={{ fontSize: 13, color: "text.secondary", opacity: 0.8 }} />
            <Typography
              variant="caption"
              sx={{
                fontFamily: "monospace",
                fontWeight: 800,
                fontSize: "0.82rem",
                color: "text.primary",
                letterSpacing: "0.5px",
              }}
            >
              {formatTime(currentTime)}
            </Typography>
          </Stack>

          <Divider orientation="vertical" flexItem sx={{ height: 14, my: "auto", opacity: 0.6 }} />

          {/* Location & Weather */}
          {loading ? (
            <CircularProgress size={12} sx={{ color: "#f97316" }} />
          ) : weather ? (
            <Stack direction="row" alignItems="center" spacing={0.7}>
              <Typography variant="body2" sx={{ fontSize: "0.95rem", lineHeight: 1 }}>
                {weather.icon}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  fontSize: "0.82rem",
                  color: "text.primary",
                }}
              >
                {weather.temp}°C
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontWeight: 600,
                  fontSize: "0.78rem",
                  maxWidth: 90,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: { xs: "none", sm: "inline-block" },
                }}
              >
                {weather.city}
              </Typography>
            </Stack>
          ) : null}
        </Box>
      </Tooltip>

      {/* Popover Sunset Weather Card Modal */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            borderRadius: "24px",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.45)",
            overflow: "hidden",
            border: "none",
          },
        }}
      >
        <WeatherCardModal
          weather={weather}
          currentTime={currentTime}
          formatTime={formatTime}
          formatDate={formatDate}
          onClose={handleClose}
          onRefresh={() => fetchWeather(true)}
          onRequestExactLocation={requestExactLocation}
          geoLoading={geoLoading}
        />
      </Popover>
    </>
  );
}

// ============================================================================
// STUNNING SUNSET WEATHER CARD (MATCHING USER'S SCREENSHOT REFERENCE)
// ============================================================================
function WeatherCardModal({
  weather,
  currentTime,
  formatTime,
  formatDate,
  onClose,
  onRefresh,
  onRequestExactLocation,
  geoLoading,
}) {
  if (!weather) return null;

  return (
    <Box
      sx={{
        width: { xs: "min(320px, calc(100vw - 24px))", sm: 460 },
        maxWidth: "calc(100vw - 24px)",
        background: "linear-gradient(135deg, #f97316 0%, #ea580c 35%, #c2410c 70%, #9a3412 100%)",
        color: "#ffffff",
        p: { xs: 3, sm: 3.5 },
        position: "relative",
        boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
      }}
    >
      {/* Top Controls: Refresh & Close */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
        <Box>
          {/* City Name */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 900,
              fontSize: { xs: "1.75rem", sm: "2.1rem" },
              letterSpacing: "-0.5px",
              lineHeight: 1.1,
              color: "#ffffff",
              textShadow: "0 2px 8px rgba(0,0,0,0.2)",
            }}
          >
            {weather.city}
          </Typography>
          {/* Current Date */}
          <Typography
            variant="body2"
            sx={{
              color: "rgba(255, 255, 255, 0.85)",
              fontWeight: 600,
              fontSize: "0.9rem",
              mt: 0.5,
            }}
          >
            {formatDate(currentTime)}
          </Typography>
        </Box>

        {/* Digital Time & Close */}
        <Stack direction="row" spacing={0.5} alignItems="center">
          {/* Real-Time Digital Watch */}
          <Box
            sx={{
              bgcolor: "rgba(0,0,0,0.2)",
              backdropFilter: "blur(8px)",
              borderRadius: "10px",
              px: 1.5,
              py: 0.5,
              border: "1px solid rgba(255,255,255,0.15)",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontFamily: "monospace",
                fontWeight: 900,
                fontSize: "0.95rem",
                color: "#ffffff",
                letterSpacing: "0.5px",
              }}
            >
              {formatTime(currentTime)}
            </Typography>
          </Box>

          <Tooltip title="Refresh live weather">
            <IconButton size="small" onClick={onRefresh} sx={{ color: "rgba(255,255,255,0.85)" }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <IconButton size="small" onClick={onClose} sx={{ color: "rgba(255,255,255,0.85)" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      {/* Main Temperature & Weather Hero */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ my: 3 }}>
        <Box>
          <Stack direction="row" alignItems="flex-start">
            <Typography
              variant="h1"
              sx={{
                fontWeight: 900,
                fontSize: { xs: "4rem", sm: "4.8rem" },
                lineHeight: 0.95,
                color: "#ffffff",
                textShadow: "0 4px 15px rgba(0,0,0,0.25)",
              }}
            >
              {weather.temp}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                fontSize: "1.8rem",
                color: "rgba(255, 255, 255, 0.9)",
                mt: 0.5,
                ml: 0.5,
              }}
            >
              °C
            </Typography>
          </Stack>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "1.2rem",
              color: "rgba(255, 255, 255, 0.95)",
              mt: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <span>{weather.icon}</span>
            <span>{weather.condition}</span>
          </Typography>

          <Typography
            variant="caption"
            sx={{ color: "rgba(255, 255, 255, 0.75)", fontWeight: 500 }}
          >
            Feels like {weather.feelsLike}°C • Humidity {weather.humidity}% • Wind {weather.windSpeed} km/h
          </Typography>
        </Box>

        {/* Location Tag */}
        <Box
          sx={{
            textAlign: "right",
            bgcolor: "rgba(255, 255, 255, 0.15)",
            backdropFilter: "blur(8px)",
            px: 1.5,
            py: 0.8,
            borderRadius: "12px",
            border: "1px solid rgba(255,255,255,0.2)",
            minWidth: { xs: 0, sm: 135 },
            maxWidth: "100%",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.5} justifyContent="flex-end">
            {weather.source === "GPS" ? (
              <MyLocationIcon sx={{ fontSize: 13, color: "#4ade80" }} />
            ) : (
              <LocationIcon sx={{ fontSize: 14, color: "#fff" }} />
            )}
            <Typography variant="caption" sx={{ fontWeight: 800, color: "#fff" }}>
              {weather.city}, {weather.country}
            </Typography>
          </Stack>
          {weather.source !== "GPS" && (
            <Button
              size="small"
              onClick={onRequestExactLocation}
              disabled={geoLoading}
              startIcon={
                geoLoading ? (
                  <CircularProgress size={10} sx={{ color: "#fff" }} />
                ) : (
                  <MyLocationIcon sx={{ fontSize: "12px !important" }} />
                )
              }
              sx={{
                color: "#fff",
                bgcolor: "rgba(0, 0, 0, 0.25)",
                "&:hover": { bgcolor: "rgba(0, 0, 0, 0.45)" },
                borderRadius: "8px",
                fontSize: "0.65rem",
                fontWeight: 700,
                textTransform: "none",
                px: 1,
                py: 0.4,
                mt: 0.8,
                border: "1px solid rgba(255, 255, 255, 0.3)",
                lineHeight: 1.2,
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              {geoLoading ? "Detecting GPS..." : "Allow GPS location for better accuracy"}
            </Button>
          )}
        </Box>
      </Stack>

      <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.2)", my: 2.5 }} />

      {/* 4-Day Forecast Columns (Matching User's Reference Screenshot) */}
      <Box>
        <Typography
          variant="caption"
          sx={{
            textTransform: "uppercase",
            letterSpacing: "1px",
            fontWeight: 800,
            color: "rgba(255, 255, 255, 0.8)",
            display: "block",
            mb: 1.5,
          }}
        >
          Next 4 Days Forecast
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 1.5,
            textAlign: "center",
          }}
        >
          {weather.forecast.map((item, idx) => (
            <Paper
              key={idx}
              elevation={0}
              sx={{
                p: 1.2,
                borderRadius: "14px",
                bgcolor: "rgba(255, 255, 255, 0.12)",
                backdropFilter: "blur(6px)",
                border: "1px solid rgba(255, 255, 255, 0.18)",
                color: "#ffffff",
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: "rgba(255, 255, 255, 0.22)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 800, color: "rgba(255, 255, 255, 0.9)", display: "block" }}
              >
                {item.day}
              </Typography>

              <Typography variant="h6" sx={{ fontWeight: 900, my: 0.3, fontSize: "1.1rem" }}>
                {item.maxTemp}°
              </Typography>

              <Typography variant="body2" sx={{ fontSize: "1.1rem", my: 0.2 }}>
                {item.icon}
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.68rem",
                  color: "rgba(255, 255, 255, 0.8)",
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {item.condition}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
